<template>
  <div class="d-flex flex-column gap-4">
    <div class="row g-4">
      <div class="col-12 col-xl-7">
        <div class="card h-100">
          <div class="card-body d-flex flex-column gap-4">
            <h4 class="font-serif mb-0">Company balance</h4>
            <div
              v-if="breakdowns.length === 0"
              class="text-body-secondary fs-9"
            >
              No ledger balance yet
            </div>
            <div
              v-for="breakdown in breakdowns"
              :key="breakdown.currency"
              class="d-flex flex-column gap-3"
            >
              <div class="d-flex flex-wrap align-items-baseline gap-2">
                <span class="fs-5 font-serif tabular-amount">{{
                  formatUnsignedAmount(breakdown.balance)
                }}</span>
                <span class="text-body-secondary">{{
                  breakdown.currency
                }}</span>
                <span
                  class="text-body-secondary"
                  data-test="balance-direction"
                  >{{ direction(breakdown.balance) }}</span
                >
              </div>
              <div
                v-if="amountSign(breakdown.balance) !== 0"
                class="balance-parts"
              >
                <div
                  v-for="part in balanceParts(breakdown)"
                  :key="part.label"
                  class="balance-part border-top py-2"
                  data-test="balance-part"
                >
                  <div>
                    <div class="fw-semibold">{{ part.label }}</div>
                    <div class="fs-9 text-body-secondary">{{ part.help }}</div>
                  </div>
                  <a
                    v-if="part.state && part.count"
                    href="#"
                    class="fs-9 text-nowrap"
                    :data-test="`${part.state}-entries`"
                    @click.prevent="showStateEntries(part.state)"
                    >{{ entryCount(part.count) }}</a
                  >
                  <span v-else class="fs-9 text-body-secondary text-nowrap">{{
                    part.state ? entryCount(0) : ""
                  }}</span>
                  <span
                    class="fw-semibold text-end text-nowrap tabular-amount"
                    >{{ alignedAmount(part.amount, breakdown.balance) }}</span
                  >
                </div>
              </div>
            </div>
            <div class="fs-9 text-body-secondary mt-auto">
              Last entry
              <span class="fw-semibold text-body">{{
                overview.last_entry_at
                  ? formatDate(overview.last_entry_at.slice(0, 10))
                  : "—"
              }}</span>
              · Payout currency
              <span class="fw-semibold text-body">{{
                overview.company.payout_currency
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-xl-5">
        <div class="card h-100">
          <div class="card-body">
            <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
              <h4 class="font-serif mb-0">Soldera side</h4>
              <span
                :title="SOLDERA_ACCOUNTS_HELP"
                class="text-body-secondary"
                role="img"
                aria-label="About the Soldera side"
              >
                <FontAwesomeIcon icon="info-circle" />
              </span>
              <span class="fs-9 text-body-secondary ms-auto"
                >Net of this company's entries</span
              >
            </div>
            <div
              v-if="overview.soldera_accounts.length === 0"
              class="fs-9 text-body-secondary"
            >
              No Soldera-side legs
            </div>
            <table v-else class="table table-sm mb-0 align-middle">
              <tbody>
                <tr
                  v-for="net in overview.soldera_accounts"
                  :key="`${net.currency}-${net.account}`"
                >
                  <td :title="net.account" data-test="soldera-account">
                    {{ humanize(net.account) }}
                  </td>
                  <td class="text-end text-nowrap tabular-amount">
                    {{ formatUnsignedAmount(net.amount) }}
                    <span class="text-body-secondary">{{ net.currency }}</span>
                  </td>
                  <td class="text-end fw-semibold debit-credit">
                    {{ amountSign(net.amount) > 0 ? "Dr" : "Cr" }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <h4 class="font-serif mb-3">Invoice history</h4>
        <div class="table-responsive">
          <table class="table table-sm table-hover mb-0 align-middle">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Type</th>
                <th>Status</th>
                <th>Date</th>
                <th class="text-end">Ledger amount</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="invoice in overview.invoices"
                :key="invoice.id"
                role="button"
                data-test="invoice-row"
                @click="showInvoiceEntries(invoice.id)"
              >
                <td :class="mutedClass(invoice)">
                  <span class="fw-semibold text-nowrap">{{
                    invoice.invoice_number
                  }}</span>
                  <span class="d-block fs-9 text-body-secondary">{{
                    linkSummary(invoice)
                  }}</span>
                </td>
                <td :class="mutedClass(invoice)">
                  {{ humanize(invoice.invoice_type) }}
                </td>
                <td :class="mutedClass(invoice)">
                  <span
                    v-if="isInactive(invoice)"
                    class="badge pill-inactive"
                    >{{ humanize(invoice.status) }}</span
                  >
                  <template v-else>{{ humanize(invoice.status) }}</template>
                </td>
                <td
                  :class="mutedClass(invoice)"
                  class="text-body-secondary text-nowrap"
                >
                  {{ formatDate(invoice.date.slice(0, 10)) }}
                </td>
                <td
                  :class="mutedClass(invoice)"
                  class="text-end fw-semibold text-nowrap tabular-amount"
                >
                  <div
                    v-for="amount in invoice.ledger_amounts"
                    :key="amount.currency"
                  >
                    {{ formatSignedAmount(amount.amount) }}
                    <span class="text-body-secondary fw-normal">{{
                      amount.currency
                    }}</span>
                  </div>
                </td>
              </tr>
              <tr v-if="overview.invoices.length === 0">
                <td colspan="5" class="text-body-secondary">
                  No linked invoices
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { formatDate } from "@/composables/formatUtils";
import {
  alignedAmount,
  amountSign,
  balanceBreakdown,
  formatSignedAmount,
  formatUnsignedAmount,
  humanize,
} from "./ledgerFormat";
import type { BalanceBreakdown } from "./ledgerFormat";
import type {
  LedgerEntry,
  LedgerEntryFilters,
  LedgerEntryState,
  LedgerInvoice,
  LedgerOverview as LedgerOverviewData,
} from "./ledgerTypes";

interface BalancePart {
  label: string;
  help: string;
  amount: string;
  /** The entry state the part links to; awaiting payment spans several and links to none. */
  state?: LedgerEntryState;
  count?: number;
}

const props = defineProps<{
  overview: LedgerOverviewData;
  entries: LedgerEntry[];
}>();

const emit = defineEmits<{
  "show:entries": [patch: Partial<LedgerEntryFilters>];
}>();

const SOLDERA_ACCOUNTS_HELP =
  "The Soldera-side legs of this company's journal entries, netted per account. Each entry balances its " +
  "company balance line against these accounts. Not Soldera's total balances.";

const breakdowns = computed(() =>
  balanceBreakdown(props.overview.balances, props.entries)
);

const balanceParts = (breakdown: BalanceBreakdown): BalancePart[] => [
  {
    label: "Open",
    help: "Payable on the company's next invoice",
    amount: breakdown.open,
    state: "open",
    count: breakdown.openCount,
  },
  {
    label: "Held back",
    help: "Referral items waiting on their source fee invoice to be paid",
    amount: breakdown.held,
    state: "held",
    count: breakdown.heldCount,
  },
  {
    label: "Awaiting payment",
    help: "Settled on invoices not paid yet, net of payments",
    amount: breakdown.awaitingPayment,
  },
];

const direction = (amount: string) => {
  const sign = amountSign(amount);
  if (sign > 0) return "owed to Soldera";
  if (sign < 0) return `owed to ${props.overview.company.name}`;
  return "settled";
};

const entryCount = (count: number) =>
  `${count} ${count === 1 ? "entry" : "entries"}`;

const isInactive = (invoice: LedgerInvoice) =>
  invoice.status === "canceled" || invoice.status === "deleted";

// The theme colours every table cell directly, so muting a row has to be set on its cells.
const mutedClass = (invoice: LedgerInvoice) => ({
  "text-body-tertiary": isInactive(invoice),
});

const linkSummary = (invoice: LedgerInvoice) => {
  const counts = invoice.link_counts;
  const entries = counts.SETTLES ?? counts.RENDERS ?? 0;
  const payments = counts.SETTLED_BY ?? 0;
  return [
    entryCount(entries),
    payments ? `${payments} ${payments === 1 ? "payment" : "payments"}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
};

const showStateEntries = (state: LedgerEntryState) => {
  emit("show:entries", {
    state,
    kind: undefined,
    invoiceId: undefined,
    expandedEntryId: undefined,
  });
};

const showInvoiceEntries = (invoiceId: number) => {
  emit("show:entries", {
    invoiceId,
    kind: undefined,
    state: undefined,
    expandedEntryId: undefined,
  });
};
</script>

<style lang="scss" scoped>
.balance-part {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(7rem, auto);
  gap: 1.5rem;
  align-items: baseline;
}

.tabular-amount {
  font-variant-numeric: tabular-nums;
}

.debit-credit {
  width: 2.5rem;
}
</style>
